"use client";

import { useEffect, useRef, useState } from "react";
import { BASE_URL } from "@/utils/api";
import Cookies from "js-cookie";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

const Certificate = ({ params }: { params: { id: string } }) => {
  const [certificateHtml, setCertificateHtml] = useState<string | null>(null);
  const courseId = params.id;
  const certificateRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    fetchCertificate(courseId);
  }, [courseId]);

  const fetchCertificate = async (courseId: string) => {
    const token = Cookies.get("login_access_token");
    if (!token) return;

    try {
      const response = await fetch(`${BASE_URL}download_certificate/${courseId}/`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) throw new Error("Failed to fetch certificate");

      const html = await response.text();
      setCertificateHtml(html);
    } catch (error: any) {
      console.error("Certificate fetch error:", error.message);
    }
  };

  const loadImages = () => {
    if (!certificateRef.current) return Promise.resolve();

    const images = certificateRef.current.querySelectorAll("img");
    const elements = certificateRef.current.querySelectorAll("*");

    const imgPromises = Array.from(images).map((img: HTMLImageElement) => {
      return new Promise<void>((resolve) => {
        img.crossOrigin = "anonymous";
        if (img.complete) {
          resolve();
        } else {
          img.onload = () => resolve();
          img.onerror = () => resolve();
        }
      });
    });

    const bgPromises = Array.from(elements).map((el: Element) => {
      return new Promise<void>((resolve) => {
        const bg = window.getComputedStyle(el).getPropertyValue("background-image");
        const urlMatch = bg.match(/url\(["']?([^"')]+)["']?\)/);
        if (urlMatch) {
          const img = new Image();
          img.crossOrigin = "anonymous";
          img.src = urlMatch[1];
          img.onload = () => resolve();
          img.onerror = () => resolve();
        } else {
          resolve();
        }
      });
    });

    return Promise.all([...imgPromises, ...bgPromises]);
  };

  const downloadCertificate = async () => {
    if (!certificateRef.current) return;

    try {
      await loadImages();
      await new Promise((resolve) => setTimeout(resolve, 1000));

      const canvas = await html2canvas(certificateRef.current, {
        scale: 2,
        useCORS: true,
        backgroundColor: "#ffffff",
        removeContainer: true,
        onclone: (clonedDoc) => {
          const images = Array.from(clonedDoc.getElementsByTagName("img"));
          for (const img of images) {
            img.crossOrigin = "anonymous";
          }
        },
      });

      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF({
        orientation: "landscape",
        unit: "px",
        format: [canvas.width, canvas.height],
      });

      pdf.addImage(imgData, "PNG", 0, 0, canvas.width, canvas.height);
      pdf.save(`certificate_${courseId}.pdf`);
    } catch (error) {
      console.error("Error capturing certificate:", error);
    }
  };

  return (
    <div>
      {certificateHtml ? (
        <>
          <div
            ref={certificateRef}
            className="w-screen h-screen flex items-center justify-center bg-white"
            dangerouslySetInnerHTML={{ __html: certificateHtml }}
          />
          <div className="flex justify-center mt-4">
            <button
              onClick={downloadCertificate}
              className="bg-blue-600 text-white px-6 py-2 rounded-lg shadow-md hover:bg-blue-700 transition"
            >
              Download as PDF
            </button>
          </div>
        </>
      ) : (
        <div className="w-full h-[80vh] flex justify-center items-center">
          <div className="loader"></div>
        </div>
      )}
    </div>
  );
};

export default Certificate;
